import React from 'react'
import { usePaginationFragment, graphql } from 'react-relay'

import { YStack, XStack, Separator, useMedia } from 'tamagui'

import { GuildCard } from '@newComponents/Guild/GuildCard'
import { Grid } from '@newComponents/Grid'
import { Button } from '@newComponents/Button/Button'
import { H2 } from '@newComponents/Text'

import { UserGuildMembershipsMembersSection_user$key } from './__generated__/UserGuildMembershipsMembersSection_user.graphql'

function useItemsPerRow() {
  const media = useMedia()
  if (media.xs) return 1
  if (media.sm) return 1
  if (media.md) return 2
  return 3
}

export const UserGuildMembershipsMembersSection = ({
  user,
}: {
  user: UserGuildMembershipsMembersSection_user$key
}) => {
  const { data, hasNext, loadNext, isLoadingNext } = usePaginationFragment(
    graphql`
      fragment UserGuildMembershipsMembersSection_user on User
      @refetchable(
        queryName: "UserGuildMembershipsMembersSectionPaginationQuery"
      )
      @argumentDefinitions(
        count: { type: "Int", defaultValue: 3 }
        cursor: { type: "Cursor" }
      ) {
        guildMemberships(
          first: $count
          after: $cursor
          condition: { membershipLevel: MEMBER }
        )
          @connection(
            key: "UserGuildMembershipsMembersSection_user_guildMemberships"
          ) {
          edges {
            node {
              guild {
                ...GuildCardNew_guild
              }
            }

            cursor
          }
        }
      }
    `,
    user
  )

  const itemsPerRow = useItemsPerRow()
  const media = useMedia()

  if (!data.guildMemberships.edges.length) {
    return null
  }

  if (media.xs) {
    return (
      <YStack gap='$4'>
        <XStack gap='$4' alignItems='center'>
          <H2 size='$4' color='$colorMuted' fontWeight='$5'>
            Guild Memberships
          </H2>
          <Separator flex={1} borderColor='$borderColor' />
        </XStack>

        <YStack gap='$6'>
          {data.guildMemberships.edges.map((edge) => {
            if (!edge.node?.guild) return null
            return (
              <GuildCard
                key={edge.cursor}
                guild={edge.node.guild}
                limitDescriptionLines={3}
              />
            )
          })}
        </YStack>

        {hasNext && (
          <Button
            secondary
            onPress={() => {
              if (isLoadingNext) return
              loadNext(3)
            }}
          >
            <Button.Text secondary>
              {isLoadingNext ? 'Loading...' : 'More'}
            </Button.Text>
          </Button>
        )}
      </YStack>
    )
  }

  const guildGroups = data.guildMemberships.edges.reduce<
    Array<Array<(typeof data.guildMemberships.edges)[number]>>
  >((memo, guild, index) => {
    if (index % itemsPerRow === 0) {
      memo.push([guild])
    } else {
      memo[memo.length - 1].push(guild)
    }

    return memo
  }, [])

  return (
    <YStack gap='$4'>
      <XStack gap='$4' alignItems='center'>
        <H2 size='$4' color='$colorMuted' fontWeight='$5'>
          Guild Memberships
        </H2>
        <Separator flex={1} borderColor='$borderColor' />
      </XStack>

      <YStack gap='$6'>
        {guildGroups.map((group, index) => {
          return (
            <XStack key={index}>
              <Grid gap='$6'>
                {group.map((edge) => {
                  if (!edge.node?.guild) return null
                  return (
                    <Grid.Column key={edge.cursor}>
                      <GuildCard
                        guild={edge.node.guild}
                        limitDescriptionLines={3}
                        expand
                      />
                    </Grid.Column>
                  )
                })}

                {Array.from({
                  length: itemsPerRow - group.length,
                }).map((_, index) => (
                  <Grid.Column key={index} />
                ))}
              </Grid>
            </XStack>
          )
        })}
      </YStack>

      {hasNext && (
        <Button
          secondary
          onPress={() => {
            if (isLoadingNext) return
            loadNext(3)
          }}
        >
          <Button.Text secondary>
            {isLoadingNext ? 'Loading...' : 'More'}
          </Button.Text>
        </Button>
      )}
    </YStack>
  )
}
